import { Dialog, SvgIcon } from '@mui/material'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import React, {
  createContext,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import kycIconSvg from 'src/assets/kyc approval.svg'
import { preloadImage } from 'src/utils/imagePreloader'
import { getAppointmentCalendarSettings } from 'src/clinic/reducers/prescription/services/appointmentCalendarSettings'
import { fetchPrescriptionViewSettings } from 'src/clinic/reducers/prescription/services/prescriptionViewSettingsThunk'
import { LoadingSpinner } from 'src/consumer/components/Loaders'
import {
  REDUX_STATUS_FAILED,
  REDUX_STATUS_LOADING,
  REDUX_STATUS_SUCCESS,
  DISPATCH_EVENT_KEYS,
} from 'src/consumer/constants'
import { getData } from 'src/consumer/services/profileService'
import { DocSideContext } from 'src/context/DocSideContextProvider'
import { getTeacherClinicAssociations } from 'src/reducers/teacherProfileData/services/clinicAssociations'
import { getTeacherProfileData } from 'src/reducers/teacherProfileData/services/teacherProfile'
import {
  selectMentorClinicAssociations,
  selectTeacherProfileData,
  setTeacherProfileData,
} from 'src/reducers/teacherProfileData/teacherProfileDataReducer'
import ClinicOnboardingComponent from '../components/ClinicOnboardingComponent/ClinicOnboardingComponent'
import { getLegalPolicies } from '../components/LegalTerms/legal_utils'
import MentorHeaderAndContent from '../components/MentorHeaderAndContent'
import PortalSwitchGateCard from '../components/KycPendingActionsPage/PortalSwitchGateCard'
import PortalSwitcherButton from '../components/MentorHeader/PortalSwitcherButton'
import MentorSidebar from '../components/MentorSidebar'
import { MentorLocalizationProvider } from '../context/MentorLocalizationContext'
import { MentorProfileContext } from '../context/MentorProfileContext'
import { MentorSetupContext } from '../context/MentorSetupContext'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
  logoutHandlerMentor,
  sleep,
} from '../helpers/utilityFunctions'
import { getMPData } from '../services'
import useMentorSignals from '../hooks/useMentorSignals'
import { validateAndSyncCheckIn, initCheckInBroadcastChannel } from 'src/clinic/components/CheckIn/checkInSyncService'
import VideoCallModal from '../components/VideoCallModal'
import CheckinRequestModal from '../components/CheckinRequestModal'
import CheckinRequestStatusModal from '../components/CheckinRequestStatusModal'
import { toast } from 'react-toastify'
import {
  KycExploreAppButton,
  KycIcon,
  KycPendingContent,
  KycPendingPaper,
  KycProceedButton,
  KycSubText,
  KycText,
  LayoutContainer,
  MentorAccessUnavailableActions,
  MentorAccessUnavailableContainer,
  MentorAccessUnavailableIcon,
  MentorAccessUnavailablePanel,
  MentorAccessUnavailablePortalSwitcher,
  MentorAccessUnavailablePortalSwitcherText,
  MentorAccessUnavailablePrimaryButton,
  MentorAccessUnavailableSecondaryButton,
  MentorAccessUnavailableText,
  MentorAccessUnavailableTitle,
} from './Styles'
import {
  getAllDomains,
  getAllRolesInBoolean,
  getClinicAppointmentsForThisClinicForMentors,
  getIsTeacherOrCreatorData,
  getUserRoles,
  isJustOnboardedTeacher,
  removeJustOnboardedTeacher,
} from './utils'
import { getAvailablePortals } from 'src/shared/config/portalConfig'
import {
  buildBrandsFromLocations,
  buildRosterAssignmentIndex,
  buildRosterRoomTargetsFromBrands,
  fetchMentorRosterAssignments,
  getRosterReminderCandidate,
} from 'src/mentor/components/MentorClinicBookingsPage/rosterAssignments'

export const MentorClinicsContext = createContext()

const ROSTER_REMINDER_STORAGE_KEY = 'mentor-roster-checkin-reminders-v1'
const ROSTER_CURRENT_REMINDER_REPEAT_MS = 20 * 60 * 1000

function readCheckedInDetailsFromStorage() {
  try {
    const storedValue = localStorage.getItem('checkedInDetails')
    return storedValue ? JSON.parse(storedValue) : null
  } catch (error) {
    console.error('Error parsing checkedInDetails from storage:', error)
    return null
  }
}

function getRosterReminderKey(candidate) {
  const assignment = candidate?.group?.primaryAssignment

  return [candidate?.type, assignment?.id || candidate?.group?.chamberRoomId, assignment?.slotDate]
    .filter(Boolean)
    .join(':')
}

function readRosterReminderState() {
  try {
    const storedValue = localStorage.getItem(ROSTER_REMINDER_STORAGE_KEY)
    return storedValue ? JSON.parse(storedValue) : {}
  } catch (error) {
    console.error('Error parsing roster reminder state:', error)
    return {}
  }
}

function shouldShowRosterReminder(candidate) {
  if (!candidate?.group?.primaryAssignment) {
    return false
  }

  const reminderState = readRosterReminderState()
  const previousReminder = reminderState?.[getRosterReminderKey(candidate)]

  if (!previousReminder?.lastShownAt) {
    return true
  }

  if (candidate?.type === 'upcoming') {
    return false
  }

  return Date.now() - previousReminder.lastShownAt >= ROSTER_CURRENT_REMINDER_REPEAT_MS
}

function markRosterReminderShown(candidate) {
  const reminderKey = getRosterReminderKey(candidate)
  if (!reminderKey) {
    return
  }

  const nextReminderState = {
    ...readRosterReminderState(),
    [reminderKey]: {
      lastShownAt: Date.now(),
    },
  }

  localStorage.setItem(ROSTER_REMINDER_STORAGE_KEY, JSON.stringify(nextReminderState))
}

function buildRosterReminderMessage(candidate) {
  const assignmentGroup = candidate?.group
  if (!assignmentGroup) {
    return ''
  }

  const locationLabel = [assignmentGroup.clinicBrandName, assignmentGroup.clinicLocationName]
    .filter(Boolean)
    .join(' > ')
  const roomLabel = assignmentGroup.chamberRoomTitle || 'assigned chamber room'
  const timeLabel = assignmentGroup.timeLabel || assignmentGroup.detailLabel || 'today'

  if (candidate?.type === 'upcoming') {
    return `${roomLabel} at ${locationLabel} starts in ${
      candidate?.minutesUntilStart ?? 'a few'
    } minutes (${timeLabel}). Tap to open your check-in page.`
  }

  if (candidate?.type === 'overdue') {
    return `You are rostered to check in at ${locationLabel} > ${roomLabel} and the slot is already live (${timeLabel}). Tap to open your check-in page.`
  }

  return `You are rostered right now at ${locationLabel} > ${roomLabel} (${timeLabel}). Tap to open your check-in page.`
}

export default function MentorLayout() {
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const teacherCreatorData = getIsTeacherOrCreatorData()
  const allRoles = getAllRolesInBoolean()
  const isDoctor = allRoles?.isDoctor
  const isClinicAdmin = allRoles?.isClinicAdmin
  const isClinicOwner = allRoles?.isClinicOwner
  const mentorProfileStoreData = useSelector(selectTeacherProfileData)
  const mentorProfileData = mentorProfileStoreData.data
  const mentorProfileLoadFailed = mentorProfileStoreData.status === REDUX_STATUS_FAILED
  const mentorProfileMissing =
    mentorProfileStoreData.status === REDUX_STATUS_SUCCESS &&
    (!mentorProfileData || !mentorProfileData?.id)
  const mentorProfileInactive =
    mentorProfileData?.enabled === false || mentorProfileData?.deleted === true
  // A transient profile re-fetch FAILURE (status FAILED) must NOT mark the portal unavailable while
  // we still hold a valid profile. The reducer keeps `data` on `.pending`, so an in-flight refresh
  // is fine — but on `.rejected` the status flips to FAILED, and treating that alone as "unavailable"
  // nulls activeMentorProfileData and trips the MentorAccessUnavailable early-return below, which
  // unmounts the whole mentor content. For the full-screen session under /mentor-home/l/* (which
  // re-fetches the profile on mount — LiveSessionPage ~L478 dispatch(getTeacherProfileData)) that
  // unmount→remount re-runs the session's Agora RTM login with the same uid → duplicate-login
  // failure (-10023). So a failed refresh only counts as unavailable when we have no usable profile.
  const mentorProfileUnavailable =
    mentorProfileInactive ||
    mentorProfileMissing ||
    (mentorProfileLoadFailed && !mentorProfileData?.id)
  const activeMentorProfileData = mentorProfileUnavailable ? null : mentorProfileData
  const isMentorProfileOperational = Boolean(activeMentorProfileData?.id)
  
  // Memoize pathname to avoid location object reference changes
  const currentPathname = useMemo(() => location?.pathname || '', [location?.pathname])
  
  const [enteringFirstTime, setIsEnteringFirstTime] = useState(true)
  // const [mentorProfileData, setMentorProfileData] = useState(null)
  const [kycPending, showKycPending] = useState(false)
  const [allLegalPolicies, setAllLegalPolicies] = useState([])

  const mentorClinicAssociationStoreData = useSelector(selectMentorClinicAssociations)
  const mentorClinicAssociationData = mentorClinicAssociationStoreData.data
  const loadingMentorClinicAssociationData =
    mentorClinicAssociationStoreData.status === REDUX_STATUS_LOADING

  const [mentorClinicChambersData, setMentorClinicChambersData] = useState()
  const [mentorChamberRoomsData, setMentorChamberRoomsData] = useState()
  const [dontShowKycModal, setDontShowKycModal] = useState(false)

  const [associatedClinicBrands, setAssociatedClinicBrands] = useState([])
  const [associatedClinicsData, setAssociatedClinicsData] = useState([])
  const [loadingAssociationsData, setLoadingAssociationData] = useState(false)

  const [tags, setTags] = useState([])
  const [userProfile, setUserProfile] = useState()
  const [modalsViewCount, setModalsViewCount] = useState({
    kyc: 0,
  })
  const [openCreateClinicPopup, setOpenCreateClinicPopup] = useState(false)

  // hideFeeModals/hidePayoutReports were retired: those surfaces are now controlled per clinic
  // brand via ClinicBrand.ownerPortalSettings (support-managed), read off associatedClinicBrands.
  const [portalViewSettings, setPortalViewSettings] = useState({
    hidePatientContactDetails: false,
  })

  const [clinicEventTypesMap, setClinicEventTypesMap] = useState()
  const [todayRosterAssignments, setTodayRosterAssignments] = useState([])
  const [rosterAssignmentsLoading, setRosterAssignmentsLoading] = useState(false)
  const [rosterClockNow, setRosterClockNow] = useState(() => dayjs())
  const [checkedInDetails, setCheckedInDetails] = useState(() => readCheckedInDetailsFromStorage())

  const { setFlowId, setButtonTopOffset, setShowDocSide } = useContext(DocSideContext)
  const deferredBootstrapRef = useRef({
    legalPolicies: false,
    tags: false,
    mentorClinicAssociation: false,
    mentorClinicChambers: false,
    mentorChamberRooms: false,
    portalViewSettings: false,
    ownerClinicAssociations: false,
    appointmentCalendarSettings: false,
    prescriptionViewSettings: false,
  })

  const MentorPublicPage = React.lazy(() =>
    import('../components/MentorPublicPage/MentorPublicPage'),
  )

  function setKycPending(state, force = false) {
    if (state === true) {
      setModalsViewCount((prev) => ({ ...prev, kyc: prev?.kyc + 1 }))
    }
    if (modalsViewCount > 0) {
      force ? showKycPending(state) : showKycPending(false)
      return
    }
    showKycPending(state)
  }

  async function updateProfileData() {
    const resp = await getMPData('/api/secure/teacher/profile')
    if (resp?.status === 200) {
      setIsEnteringFirstTime(isJustOnboardedTeacher())
      setTeacherProfileData(resp?.data)
    }
  }

  async function getAndSetClinicAppointmentTypesData(clinicArray) {
    clinicArray.forEach(async (clinicItem) => {
      if (!clinicItem?.useClinicAppointmentTypesForBooking) {
        return
      }

      const clinicAppointmentsForThisClinic = await getClinicAppointmentsForThisClinicForMentors(
        clinicItem?.id,
        clinicItem?.clinicBrandId,
      )

      if (!clinicAppointmentsForThisClinic) {
        return
      }

      setClinicEventTypesMap((prev) => ({
        ...prev,
        [clinicItem?.id]: clinicAppointmentsForThisClinic,
      }))
    })
  }

  async function getAndSetLegalPolicies() {
    const allLegalPolicies = await getLegalPolicies()
    if (allLegalPolicies) {
      setAllLegalPolicies(allLegalPolicies)
    }
  }

  async function getTags() {
    const resp = await getAllDomains()
    if (resp.status === 200) {
      setTags(resp.data)
    }
  }

  const getMentorClinicAssociation = useCallback(async () => {
    await dispatch(getTeacherClinicAssociations())
  }, [dispatch])

  async function getMentorClinicChambersData() {
    const resp = await getMPData('/api/secure/teacher/clinic-chamber/list')
    if (resp?.status === 200) {
      setMentorClinicChambersData(resp?.data)
    }
  }

  async function getMentorChamberRoomsData() {
    const resp = await getMPData('/api/secure/teacher/chamber-room/list')
    if (resp?.status === 200) {
      setMentorChamberRoomsData(resp?.data)
    }
  }

  async function getMentorProfile({ forceFetch = false } = {}) {
    console.log({ forceFetch })
    dispatch(getTeacherProfileData({ forceFetch }))

    // const resp = await getMPData('/api/secure/teacher/profile')
    // if (resp?.status === 200) {
    //   setMentorProfileData(resp?.data)
    // }
  }

  async function getPortalViewSettings() {
    const resp = await getData('/api/secure/practitioner/emr-settings/settings/portal-view-settings')
    if (resp?.status === 200) {
      if (isCommonResponseSuccessfulV2(resp)) {
        const respData = getDataMapFromCommonResponse(resp)
        setPortalViewSettings((prev) => ({
          ...prev,
          hidePatientContactDetails:
            respData?.['show-patient-contact-details-on-mentor-portal'] === false,
        }))
      }
    }
  }

  async function endOnboardingFlow() {
    setKycPending(true, true)
    setIsEnteringFirstTime(false)
    removeJustOnboardedTeacher()
    // removeOnboardingTypeFromLocalStorage()
  }

  function showKycModal() {
    setKycPending(true)
  }

  function showCreateClinicPopup() {
    setOpenCreateClinicPopup(true)
  }

  function handleClose() {
    setKycPending(false)
  }

  function initializePrescriptionViewSettings() {
    if (getAllRolesInBoolean().isDoctor) {
      dispatch(fetchPrescriptionViewSettings())
    }
  }

  function extractClinicBrands(payload) {
    if (Array.isArray(payload)) return payload
    if (!payload || typeof payload !== 'object') return []
    // Direct known shapes
    if (Array.isArray(payload?.data)) return payload.data
    if (Array.isArray(payload?.clinicBrands)) return payload.clinicBrands
    if (Array.isArray(payload?.data?.clinicBrands)) return payload.data.clinicBrands
    // Fallback: pick the first array value in the payload
    const firstArrayValue = Object.values(payload).find((val) => Array.isArray(val))
    return Array.isArray(firstArrayValue) ? firstArrayValue : []
  }

  async function getAndSetOwnerClinicBrandAssociations() {
    const respTHREE = await getData('/api/secure/cp/clinic-brand')
    if (respTHREE?.status === 200) {
      const payload = respTHREE?.data
      const brands = extractClinicBrands(payload)
      setAssociatedClinicBrands(brands)
    }
  }

  async function getAndSetOwnerClinicAssociations() {
    const respTWO = await getData('/api/secure/cp/clinic-profile')
    if (respTWO?.status === 200) {
      setAssociatedClinicsData(respTWO?.data || [])
      getAndSetClinicAppointmentTypesData(respTWO?.data)
    }
  }

  async function setupClinicAssociationData() {
    setLoadingAssociationData(true)
    try {
      await Promise.all([getAndSetOwnerClinicBrandAssociations(), getAndSetOwnerClinicAssociations()])
    } finally {
      setLoadingAssociationData(false)
    }
  }

  async function ensureDeferredBootstrap(key, loader) {
    if (deferredBootstrapRef.current[key]) {
      return
    }

    deferredBootstrapRef.current[key] = true

    try {
      await loader()
    } catch (error) {
      deferredBootstrapRef.current[key] = false
      console.error(`[MentorLayout] Failed to bootstrap ${key}:`, error)
    }
  }

  function matchesRoutePrefix(routePrefix) {
    return currentPathname === routePrefix || currentPathname.startsWith(`${routePrefix}/`)
  }

  function matchesAnyRoutePrefix(routePrefixes = []) {
    return routePrefixes.some((routePrefix) => matchesRoutePrefix(routePrefix))
  }

  const isMentorHomeLandingRoute =
    currentPathname === '/mentor-home' || currentPathname === '/mentor-home/'
  const isClinicAssociationsLandingRoute =
    currentPathname === '/mentor-home/my-clinic-associations' ||
    currentPathname === '/mentor-home/my-clinic-associations/' ||
    currentPathname === '/mentor-home/stats' ||
    currentPathname === '/mentor-home/manage-my-bookings'
  const isClinicAssociationsDetailRoute = currentPathname.startsWith(
    '/mentor-home/my-clinic-associations/',
  )
  const shouldLoadLegalPolicies = !matchesAnyRoutePrefix([
    '/mentor-home/my-clinic-associations',
    '/mentor-home/stats',
    '/mentor-home/manage-my-bookings',
    '/mentor-home/join-chamber-room',
    '/mentor-home/join-as-auditor',
    '/mentor-home/settings',
  ])
  const shouldLoadTags = matchesAnyRoutePrefix([
    '/mentor-home/profile',
    '/mentor-home/homework-plans',
    '/mentor-home/kyc-pending-actions',
  ])
  const shouldLoadMentorClinicChambersData =
    teacherCreatorData?.isTeacher && isMentorProfileOperational && isClinicAssociationsLandingRoute
  const shouldLoadMentorChamberRoomsData =
    teacherCreatorData?.isTeacher && isMentorProfileOperational && isClinicAssociationsDetailRoute
  const shouldLoadClinicOwnerAssociations =
    isMentorProfileOperational &&
    (isDoctor || isClinicAdmin || isClinicOwner) &&
    (isMentorHomeLandingRoute ||
      matchesAnyRoutePrefix([
        '/mentor-home/preferences-and-settings',
        '/mentor-home/stats',
        // Clinic bookings page (incl. the per-clinic detail route) hosts the inline owner "Clinic
        // Stats" tab, which needs associatedClinicBrands — otherwise it shows "No clinic brand available".
        '/mentor-home/my-clinic-associations',
        '/mentor-home/fee-collection-report',
        '/mentor-home/clinic-payout-reports',
        '/mentor-home/payout-reports',
        '/mentor-home/business-models',
        '/mentor-home/business-and-invoices',
        // Integrations page needs associatedClinicBrands for its brand selector.
        '/mentor-home/integrations',
      ]))
  const shouldLoadPortalViewSettings =
    isMentorProfileOperational &&
    teacherCreatorData?.isTeacher &&
    (isMentorHomeLandingRoute ||
      matchesAnyRoutePrefix([
        '/mentor-home/preferences-and-settings',
        '/mentor-home/fee-collection-report',
        '/mentor-home/clinic-payout-reports',
        '/mentor-home/business-models',
        '/mentor-home/manage-my-bookings',
        '/mentor-home/join-chamber-room',
        '/mentor-home/my-clinic-associations',
      ]))
  const shouldLoadAppointmentCalendarSettings =
    isMentorProfileOperational &&
    (isDoctor || allRoles?.isTherapist) &&
    // my-clinic-associations needs these settings too: the booking-listing display flags and the
    // practitioner's configured default tab (calendar vs appointments) both come from here.
    matchesAnyRoutePrefix(['/mentor-home/settings', '/mentor-home/my-clinic-associations'])
  const shouldLoadPrescriptionViewSettings =
    isMentorProfileOperational &&
    isDoctor &&
    matchesAnyRoutePrefix([
      '/mentor-home/prescription-templates',
      '/mentor-home/prescription',
      '/mentor-home/therapy-report',
      '/mentor-home/screening-report',
    ])

  useEffect(() => {
    setIsEnteringFirstTime(isJustOnboardedTeacher())
    
    // Initialize BroadcastChannel for multi-tab sync
    initCheckInBroadcastChannel()
    
    // Validate and sync check-in status on page load
    const validateCheckIn = async () => {
      try {
        const result = await validateAndSyncCheckIn()
        console.log('[MentorLayout] Check-in validation result:', result)
      } catch (error) {
        console.error('[MentorLayout] Error validating check-in:', error)
      }
    }
    
    // Validate after a short delay to allow other initialization to complete
    const validationTimeout = setTimeout(validateCheckIn, 1000)
    
    if (teacherCreatorData?.isTeacher || teacherCreatorData?.isCreator) {
      getMentorProfile()
    }
    
    return () => {
      clearTimeout(validationTimeout)
    }
  }, [])

  useEffect(() => {
    if (!teacherCreatorData?.isTeacher || !isMentorProfileOperational) {
      return
    }

    ensureDeferredBootstrap('mentorClinicAssociation', getMentorClinicAssociation)
  }, [getMentorClinicAssociation, isMentorProfileOperational, teacherCreatorData?.isTeacher])

  useEffect(() => {
    if (!(teacherCreatorData?.isTeacher || teacherCreatorData?.isCreator)) {
      return
    }

    if (shouldLoadLegalPolicies) {
      ensureDeferredBootstrap('legalPolicies', getAndSetLegalPolicies)
    }

    if (shouldLoadTags) {
      ensureDeferredBootstrap('tags', getTags)
    }

    if (shouldLoadMentorClinicChambersData) {
      ensureDeferredBootstrap('mentorClinicChambers', getMentorClinicChambersData)
    }

    if (shouldLoadMentorChamberRoomsData) {
      ensureDeferredBootstrap('mentorChamberRooms', getMentorChamberRoomsData)
    }

    if (shouldLoadPortalViewSettings) {
      ensureDeferredBootstrap('portalViewSettings', getPortalViewSettings)
    }

    if (shouldLoadClinicOwnerAssociations) {
      ensureDeferredBootstrap('ownerClinicAssociations', setupClinicAssociationData)
    }

    if (shouldLoadAppointmentCalendarSettings) {
      ensureDeferredBootstrap('appointmentCalendarSettings', async () => {
        await dispatch(
          getAppointmentCalendarSettings({
            appointmentCalendarSettingsGetApiSignature:
              '/api/secure/practitioner/emr-settings/settings/appointment-calendar-settings',
          }),
        )
      })
    }

    if (shouldLoadPrescriptionViewSettings) {
      ensureDeferredBootstrap('prescriptionViewSettings', async () => {
        initializePrescriptionViewSettings()
      })
    }
  }, [
    currentPathname,
    dispatch,
    shouldLoadLegalPolicies,
    shouldLoadTags,
    shouldLoadMentorClinicChambersData,
    shouldLoadMentorChamberRoomsData,
    shouldLoadPortalViewSettings,
    shouldLoadClinicOwnerAssociations,
    shouldLoadAppointmentCalendarSettings,
    shouldLoadPrescriptionViewSettings,
    teacherCreatorData?.isCreator,
    teacherCreatorData?.isTeacher,
  ])

  // Redirect logic: Check KYC status and route accordingly
  useEffect(() => {
    if (mentorProfileUnavailable) {
      return
    }

    if (!loadingMentorClinicAssociationData && mentorProfileData) {
      const isKycPendingPage = currentPathname.includes('/kyc-pending-actions')
      const kycStatus = mentorProfileData?.kycStatus
      const isKycApproved = kycStatus === 'approved'
      
      // Only fully onboarded practitioners (ROLE_TEACHER side) get hard-gated by
      // KYC. ROLE_CREATOR means "applicant/prospect"; if that user also has
      // kiosk, clinic-assistant, or wellness roles, mentor should not become the
      // sticky default that traps them on this page.
      if (teacherCreatorData?.isTeacher && !isKycApproved && !isKycPendingPage && !enteringFirstTime) {
        navigate('/mentor-home/kyc-pending-actions', { replace: true })
        return
      }
      
      // If KYC is approved and user is on kyc-pending-actions, redirect to appropriate page
      if (isKycApproved && isKycPendingPage) {
        const roles = getAllRolesInBoolean()
        const hasClinicAccessRole = roles.isDoctor || roles.isTherapist || roles.isScreener
        
        if (hasClinicAccessRole) {
          navigate('/mentor-home/my-clinic-associations', { replace: true })
        } else {
          navigate('/mentor-home/preferences-and-settings', { replace: true })
        }
        return
      }
      
      // Navigate based on user roles for base mentor-home route
      if ((currentPathname === '/mentor-home' || currentPathname === '/mentor-home/') && isKycApproved) {
        const roles = getAllRolesInBoolean()
        const hasClinicAccessRole = roles.isDoctor || roles.isTherapist || roles.isScreener
        
        if (hasClinicAccessRole) {
          navigate('/mentor-home/my-clinic-associations', { replace: true })
        } else {
          navigate('/mentor-home/preferences-and-settings', { replace: true })
        }
      }
    }
  }, [
    currentPathname,
    enteringFirstTime,
    loadingMentorClinicAssociationData,
    mentorClinicAssociationData,
    mentorProfileData,
    mentorProfileUnavailable,
    navigate,
    teacherCreatorData?.isTeacher,
  ])

  // Don't show modal automatically - redirect to kyc-pending-actions page instead
  // The modal is now shown on the dedicated kyc-pending-actions page
  useEffect(() => {
    if (mentorProfileData) {
      // Keep modal state false - we redirect to kyc-pending-actions page instead
      setKycPending(false)
      setDontShowKycModal(false)
    }
  }, [mentorProfileData])

  // Preload KYC approval SVG for LCP optimization
  // This image is shown in the KYC pending modal which appears on page load for users with pending KYC
  useEffect(() => {
    // Preload the KYC SVG since it's likely to be the LCP element when modal is shown
    // kycIconSvg is a Vite-processed asset URL (e.g., /src/assets/kyc%20approval.svg in dev, hashed path in prod)
    if (kycIconSvg && typeof kycIconSvg === 'string') {
      preloadImage(kycIconSvg, {
        as: 'image',
        fetchpriority: 'high',
      })
    }
  }, [])

  useEffect(() => {
    localStorage.setItem('lastChosenFlow', 'mentor')
    sessionStorage.setItem('chosenSomeFlowInThisSession', true)

    // Ensure DocSide is visible and set flowId for mentor portal
    setShowDocSide(true)
    setFlowId('mentor-portal')
    // Note: We don't clear flowId or showDocSide on unmount to ensure DocSide remains visible
    // Individual pages can override these if needed
  }, [setFlowId, setShowDocSide])

  const noSidebarPagesPaths = [
    // '/mentor-home/l/:id' — the full-screen live session (mirrors the consumer /l/ page);
    // matches on the bare 'l' path segment → no sidebar + no content padding, same as the chamber.
    'l',
    'join-chamber-room',
    'settings',
    'join-as-auditor',
    'my-clinic-associations',
    'manage-my-bookings',
    'preferences-and-settings',
    'kyc-pending-actions',
  ]

  const allowRenderWhileAssociationsLoad = useMemo(
    () =>
      currentPathname.includes('/mentor-home/my-clinic-associations') ||
      currentPathname.includes('/mentor-home/manage-my-bookings') ||
      currentPathname.includes('/mentor-home/stats') ||
      // Keep the full-screen live session (/mentor-home/l/*) mounted while clinic associations
      // load. Otherwise the LOADING phase swaps LayoutContainer -> LoadingSpinner and unmounts the
      // session; its remount re-runs the Agora RTM login with the same uid before the prior
      // logout lands -> SAME_UID_LOGIN (-10023). The session subtree must keep a stable mount
      // identity across the associations-load toggle.
      currentPathname.startsWith('/mentor-home/l/'),
    [currentPathname],
  )

  // Initialize mentor signals handler
  const {
    videoCallModalData,
    closeVideoCallModal,
    incomingRequestModalData,
    outgoingRequestModalData,
    incomingRequestLoading,
    requestClockNow,
    closeIncomingRequestModal,
    closeOutgoingRequestModal,
    handleIncomingRequestCheckout,
    handleIncomingRequestIgnore,
    openRequestedRoom,
  } = useMentorSignals(activeMentorProfileData)

  const mentorAssociationBrands = useMemo(
    () => buildBrandsFromLocations(mentorClinicAssociationData || []),
    [mentorClinicAssociationData],
  )
  const rosterRoomTargets = useMemo(
    () => buildRosterRoomTargetsFromBrands(mentorAssociationBrands),
    [mentorAssociationBrands],
  )
  const rosterAssignmentIndex = useMemo(
    () => buildRosterAssignmentIndex(todayRosterAssignments, rosterClockNow),
    [rosterClockNow, todayRosterAssignments],
  )

  const fetchFallbackRosterRoomTargets = useCallback(async () => {
    const response = await getMPData('/api/secure/teacher/associated-chamber-rooms/all')
    if (response?.status !== 200 || !isCommonResponseSuccessfulV2(response)) {
      return []
    }

    const associatedRooms = getDataMapFromCommonResponse(response)?.chamberRooms || []
    const locationById = new Map(
      (mentorClinicAssociationData || []).map((locationItem) => [String(locationItem?.id), locationItem]),
    )

    return associatedRooms
      .filter((roomItem) => Boolean(roomItem?.id))
      .map((roomItem) => {
        const relatedLocation = locationById.get(String(roomItem?.clinicLocationId))

        return {
          roomId: roomItem?.id,
          chamberRoomTitle: roomItem?.title || roomItem?.name || 'Room unit',
          clinicBrandId: roomItem?.clinicBrandId || relatedLocation?.clinicBrandId,
          clinicBrandName:
            roomItem?.clinicBrandName || relatedLocation?.clinicBrandName || 'Clinic brand',
          clinicLocationId: roomItem?.clinicLocationId || relatedLocation?.id,
          clinicLocationName:
            roomItem?.clinicLocationName ||
            relatedLocation?.name ||
            relatedLocation?.clinicLocationName ||
            'Clinic location',
        }
      })
  }, [mentorClinicAssociationData])

  const refreshTodayRosterAssignments = useCallback(async () => {
    if (!teacherCreatorData?.isTeacher || !activeMentorProfileData?.id) {
      setTodayRosterAssignments([])
      setRosterAssignmentsLoading(false)
      return
    }

    let effectiveRoomTargets = rosterRoomTargets
    if (effectiveRoomTargets.length === 0) {
      effectiveRoomTargets = await fetchFallbackRosterRoomTargets()
    }

    if (effectiveRoomTargets.length === 0) {
      setTodayRosterAssignments([])
      setRosterAssignmentsLoading(false)
      return
    }

    setRosterAssignmentsLoading(true)

    try {
      const nextAssignments = await fetchMentorRosterAssignments({
        mentorId: activeMentorProfileData.id,
        roomTargets: effectiveRoomTargets,
      })

      setTodayRosterAssignments(nextAssignments)
    } catch (error) {
      console.error('Error fetching today roster assignments:', error)
      setTodayRosterAssignments([])
    } finally {
      setRosterAssignmentsLoading(false)
    }
  }, [
    activeMentorProfileData?.id,
    fetchFallbackRosterRoomTargets,
    rosterRoomTargets,
    teacherCreatorData?.isTeacher,
  ])

  // Memoize context values to prevent unnecessary re-renders
  // Must be at top level before any conditional returns
  const mentorProfileContextValue = useMemo(
    () => ({
      mentorProfileData: activeMentorProfileData,
      allLegalPolicies,
      refreshLegalPolicies: getAndSetLegalPolicies,
      updateProfileData: async () => {
        await getMentorProfile({ forceFetch: true })
      },
      enteringFirstTime,
      setDontShowKycModal,
      showKycModal: setKycPending,
      endOnboardingFlow,
      mentorClinicAssociationData,
      mentorClinicChambersData,
      mentorChamberRoomsData,
      portalViewSettings,
      todayRosterAssignments,
      rosterAssignmentsLoading,
      rosterAssignmentIndex,
      refreshTodayRosterAssignments,
    }),
    [
      // Only depend on actual data values, not function references
      // Use JSON.stringify for complex objects to ensure stable comparison
      activeMentorProfileData?.id,
      activeMentorProfileData?.userId,
      Array.isArray(allLegalPolicies) ? allLegalPolicies.length : 0,
      enteringFirstTime,
      Array.isArray(mentorClinicAssociationData) ? mentorClinicAssociationData.length : 0,
      Array.isArray(mentorClinicChambersData) ? mentorClinicChambersData.length : 0,
      Array.isArray(mentorChamberRoomsData) ? mentorChamberRoomsData.length : 0,
      portalViewSettings?.hidePatientContactDetails,
      todayRosterAssignments,
      rosterAssignmentsLoading,
      rosterAssignmentIndex,
      refreshTodayRosterAssignments,
    ],
  )

  const mentorClinicsContextValue = useMemo(
    () => ({
      associatedClinicBrands,
      associatedClinicsData,
      clinicEventTypesMap,
      loadingAssociationsData,
    }),
    [
      associatedClinicBrands?.length,
      associatedClinicsData?.length,
      clinicEventTypesMap,
      loadingAssociationsData,
    ],
  )

  const mentorSetupContextValue = useMemo(() => ({ allTags: tags }), [tags?.length])

  // Memoize removeLeftMargins at top level before any conditional returns
  const removeLeftMargins = useMemo(
    () =>
      !!currentPathname
        ?.split('/')
        .some((path) => noSidebarPagesPaths.includes(path)),
    [currentPathname],
  )

  useEffect(() => {
    if (!teacherCreatorData?.isTeacher) {
      setTodayRosterAssignments([])
      setRosterAssignmentsLoading(false)
      return
    }

    refreshTodayRosterAssignments()
  }, [refreshTodayRosterAssignments, teacherCreatorData?.isTeacher])

  useEffect(() => {
    if (!teacherCreatorData?.isTeacher) {
      return undefined
    }

    const intervalId = setInterval(() => {
      if (!document.hidden) {
        refreshTodayRosterAssignments()
      }
    }, 5 * 60 * 1000)

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        refreshTodayRosterAssignments()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      clearInterval(intervalId)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [refreshTodayRosterAssignments, teacherCreatorData?.isTeacher])

  useEffect(() => {
    const intervalId = setInterval(() => {
      setRosterClockNow(dayjs())
    }, 60 * 1000)

    return () => {
      clearInterval(intervalId)
    }
  }, [])

  useEffect(() => {
    const syncCheckedInDetails = () => {
      setCheckedInDetails(readCheckedInDetailsFromStorage())
    }

    const handleCheckedIn = (event) => {
      const usageLog = event?.detail?.usageLog
      if (usageLog) {
        setCheckedInDetails(usageLog)
        return
      }

      syncCheckedInDetails()
    }

    const handleCheckedOut = () => {
      setCheckedInDetails(null)
    }

    syncCheckedInDetails()

    window.addEventListener('storage', syncCheckedInDetails)
    window.addEventListener(DISPATCH_EVENT_KEYS.userCheckedIn, handleCheckedIn)
    window.addEventListener(DISPATCH_EVENT_KEYS.userCheckedOut, handleCheckedOut)

    const intervalId = setInterval(syncCheckedInDetails, 5000)

    return () => {
      window.removeEventListener('storage', syncCheckedInDetails)
      window.removeEventListener(DISPATCH_EVENT_KEYS.userCheckedIn, handleCheckedIn)
      window.removeEventListener(DISPATCH_EVENT_KEYS.userCheckedOut, handleCheckedOut)
      clearInterval(intervalId)
    }
  }, [])

  useEffect(() => {
    if (!teacherCreatorData?.isTeacher || !activeMentorProfileData?.id) {
      return
    }

    const reminderCandidate = getRosterReminderCandidate({
      assignmentIndex: rosterAssignmentIndex,
      checkedInDetails,
      now: rosterClockNow,
    })

    if (!reminderCandidate || !shouldShowRosterReminder(reminderCandidate)) {
      return
    }

    const reminderMessage = buildRosterReminderMessage(reminderCandidate)
    if (!reminderMessage) {
      return
    }

    markRosterReminderShown(reminderCandidate)

    const toastId = `roster-checkin:${getRosterReminderKey(reminderCandidate)}`
    if (toast.isActive(toastId)) {
      return
    }

    const toastMethod = reminderCandidate?.type === 'upcoming' ? toast.info : toast.warn

    toastMethod(reminderMessage, {
      toastId,
      autoClose: reminderCandidate?.type === 'upcoming' ? 12000 : false,
      closeOnClick: true,
      onClick: () => navigate('/mentor-home/my-clinic-associations'),
    })
  }, [
    activeMentorProfileData?.id,
    checkedInDetails,
    navigate,
    rosterAssignmentIndex,
    rosterClockNow,
    teacherCreatorData?.isTeacher,
  ])

  if ((teacherCreatorData?.isTeacher || teacherCreatorData?.isCreator) && mentorProfileUnavailable) {
    return (
      <MentorAccessUnavailable
        isInactive={mentorProfileInactive}
        isMissing={mentorProfileMissing}
      />
    )
  }

  // On the full-screen live session (/mentor-home/l/:id) — the general consultation flow that
  // practitioners are redirected into — suppress the global mentor-portal modals (incoming call,
  // check-in requests, KYC nag, clinic onboarding). They would otherwise cover an active patient
  // consultation, and were never present on the old consumer /l/ session. The chamber
  // (/mentor-home/join-chamber-room/.../l/…) and every other mentor page keep them.
  const onFullScreenLiveSession = Boolean(currentPathname?.startsWith('/mentor-home/l/'))
  const globalMentorModals = onFullScreenLiveSession ? null : (
    <>
      <VideoCallModal
        open={!!videoCallModalData}
        bookedEventInfo={videoCallModalData?.bookedEventInfo}
        status={videoCallModalData?.status}
        receivedAt={videoCallModalData?.receivedAt}
        onClose={closeVideoCallModal}
      />
      <CheckinRequestModal
        open={!!incomingRequestModalData}
        requestData={incomingRequestModalData}
        onCheckout={handleIncomingRequestCheckout}
        onIgnore={handleIncomingRequestIgnore}
        onClose={closeIncomingRequestModal}
        loading={incomingRequestLoading}
        nowTs={requestClockNow}
      />
      <CheckinRequestStatusModal
        open={!!outgoingRequestModalData}
        requestData={outgoingRequestModalData}
        nowTs={requestClockNow}
        onClose={closeOutgoingRequestModal}
        onOpenRequestedRoom={openRequestedRoom}
      />
      <MentorKycPendingModal
        open={kycPending}
        mentorProfileData={activeMentorProfileData}
        onClose={handleClose}
        openCreateClinicPopup={showCreateClinicPopup}
      />
      <ClinicOnboardingComponent
        open={openCreateClinicPopup}
        onClose={() => {
          setOpenCreateClinicPopup(false)
        }}
      />
    </>
  )

  if (teacherCreatorData?.isTeacher || teacherCreatorData?.isCreator) {
    return (
      <MentorLocalizationProvider>
        <MentorProfileContext.Provider value={mentorProfileContextValue}>
          <MentorClinicsContext.Provider value={mentorClinicsContextValue}>
            <MentorSetupContext.Provider value={mentorSetupContextValue}>
              {loadingMentorClinicAssociationData ? (
                allowRenderWhileAssociationsLoad && activeMentorProfileData ? (
                  <LayoutContainer>
                    {!currentPathname
                      ?.split('/')
                      .some((path) => noSidebarPagesPaths.includes(path)) && (
                      <MentorSidebar associatedClinicBrands={associatedClinicBrands} />
                    )}
                    <MentorHeaderAndContent
                      key="mentor-header-content"
                      removeLeftMargins={removeLeftMargins}
                    />
                    {globalMentorModals}
                  </LayoutContainer>
                ) : (
                  <LoadingSpinner />
                )
              ) : activeMentorProfileData ? (
                <LayoutContainer>
                  {!currentPathname
                    ?.split('/')
                    .some((path) => noSidebarPagesPaths.includes(path)) && (
                    <MentorSidebar associatedClinicBrands={associatedClinicBrands} />
                  )}
                  <MentorHeaderAndContent key="mentor-header-content" removeLeftMargins={removeLeftMargins} />
                  {globalMentorModals}
                </LayoutContainer>
              ) : (
                <LoadingSpinner />
                  )}
                </MentorSetupContext.Provider>
              </MentorClinicsContext.Provider>
            </MentorProfileContext.Provider>
          </MentorLocalizationProvider>
    )
  } else {
    return (
      <div style={{ fontFamily: 'Outfit' }}>
        <Suspense fallback={<LoadingSpinner />}>
          <MentorPublicPage />
        </Suspense>
      </div>
    )
  }
}

function MentorAccessUnavailable({ isInactive, isMissing }) {
  const navigate = useNavigate()
  const showPortalSwitcher = useMemo(() => getAvailablePortals(getUserRoles()).length > 1, [])

  const message = isInactive
    ? 'This account has mentor portal roles, but the linked practitioner profile is inactive. Please contact the AUMHUM team or a clinic admin to restore practitioner access.'
    : isMissing
    ? 'This account has mentor portal roles, but no active practitioner profile is linked yet. Please contact the AUMHUM team or a clinic admin to finish setup.'
    : 'We could not verify an active practitioner profile for this account. Please contact the AUMHUM team or a clinic admin to restore access.'

  const handleSignOut = () => {
    logoutHandlerMentor()
    navigate('/mentor-home/login', { replace: true })
  }

  return (
    <MentorAccessUnavailableContainer>
      <MentorAccessUnavailablePanel>
        <MentorAccessUnavailableIcon>
          <ErrorOutlineIcon />
        </MentorAccessUnavailableIcon>
        <MentorAccessUnavailableTitle>Mentor portal access needs attention</MentorAccessUnavailableTitle>
        <MentorAccessUnavailableText>{message}</MentorAccessUnavailableText>
        <MentorAccessUnavailableText>
          Your wellness app access is still available from the main AUMHUM home.
        </MentorAccessUnavailableText>
        {showPortalSwitcher && (
          <MentorAccessUnavailablePortalSwitcher>
            <MentorAccessUnavailablePortalSwitcherText>
              Switch portal
            </MentorAccessUnavailablePortalSwitcherText>
            <PortalSwitcherButton />
          </MentorAccessUnavailablePortalSwitcher>
        )}
        <MentorAccessUnavailableActions>
          <MentorAccessUnavailablePrimaryButton
            onClick={() => {
              // Match the portal-switcher contract: explicitly opt this
              // session into the wellness shell so RootLanding +
              // WellnessHomeGate don't bounce this practitioner back to
              // /mentor-home (which is where they came from and which
              // we already know is unavailable).
              try {
                sessionStorage.setItem('explicitlyChoseConsumerPortal', 'true')
                sessionStorage.setItem('chosenSomeFlowInThisSession', 'true')
                sessionStorage.removeItem('portalAutoNavigatedToAtFlow')
                localStorage.removeItem('lastChosenFlow')
              } catch {
                // sessionStorage unavailable — the redirect still works,
                // user just has one extra bounce inside WellnessHomeGate.
              }
              navigate('/wellness', { replace: true })
            }}
          >
            Open wellness app
          </MentorAccessUnavailablePrimaryButton>
          <MentorAccessUnavailableSecondaryButton onClick={handleSignOut}>
            Sign out
          </MentorAccessUnavailableSecondaryButton>
        </MentorAccessUnavailableActions>
      </MentorAccessUnavailablePanel>
    </MentorAccessUnavailableContainer>
  )
}

function MentorKycPendingModal({ onClose, openCreateClinicPopup, mentorProfileData, ...rest }) {
  const navigate = useNavigate()
  const showSetupClinicButton = useMemo(() => {
    if (!mentorProfileData?.categories?.includes('doctor')) {
      return false
    }
    // if (!getAllRolesInBoolean().isDoctor) {
    //   return false
    // }
    if (
      getAllRolesInBoolean().isClinicProspect ||
      getAllRolesInBoolean().isClinicOwner ||
      getAllRolesInBoolean().isClinicAdmin
    ) {
      return false
    }
    return true
  }, [mentorProfileData])

  return (
      <Dialog sx={{ backdropFilter: 'blur(4px)' }} PaperComponent={KycPendingPaper} {...rest}>
      <KycPendingContent>
        <KycIcon 
          src={kycIconSvg} 
          alt="KYC Approval Pending"
          loading="eager"
          fetchpriority="high"
          decoding="async"
        />
        <div className="d-flex flex-column justify-content-center align-items-center gap-1">
          <KycText>KYC Approval Pending</KycText>
          <KycSubText>
            Your KYC approval is pending. We will send you an email when it is approved.<br></br> In
            the meantime, you can edit your profile.
          </KycSubText>
        </div>
        <PortalSwitchGateCard compact />
        <div className="d-flex flex-column gap-2">
          {showSetupClinicButton && (
            <KycProceedButton
              sx={{
                background: '#1b1ba4',
                padding: '10px 35px',
              }}
              onClick={() => {
                openCreateClinicPopup()
                onClose()
              }}
            >
              <span>Set up your clinic on AUMHUM</span>
              <SvgIcon>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  // className="icon icon-tabler icons-tabler-outline icon-tabler-prescription"
                >
                  <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                  <path d="M6 19v-16h4.5a4.5 4.5 0 1 1 0 9h-4.5" />
                  <path d="M19 21l-9 -9" />
                  <path d="M13 21l6 -6" />
                </svg>
              </SvgIcon>
            </KycProceedButton>
          )}
          <KycProceedButton onClick={onClose}>Edit Profile</KycProceedButton>
          <KycExploreAppButton
            disableRipple
            sx={{ padding: '10px 0px !important' }}
            onClick={() => {
              onClose()
              sleep(400)
              // Explicit wellness opt-in so RootLanding doesn't bounce
              // this practitioner straight back to /mentor-home.
              try {
                sessionStorage.setItem('explicitlyChoseConsumerPortal', 'true')
                sessionStorage.setItem('chosenSomeFlowInThisSession', 'true')
                sessionStorage.removeItem('portalAutoNavigatedToAtFlow')
                localStorage.removeItem('lastChosenFlow')
              } catch { /* private-browsing: WellnessHomeGate will bounce once */ }
              navigate('/wellness')
            }}
          >
            Explore AUMHUM Wellness App
          </KycExploreAppButton>
        </div>
      </KycPendingContent>
    </Dialog>
  )
}
